import React, { useRef, useEffect, useState } from "react";
import "./cameraComponent.scss";
import { Button, Spin } from "antd";
import { DocumentUploadConstants } from "../../constants/shared/documentUploadConstants";
import Notification from "../Notification";
import { UPLOAD_NOTIFICATION_CONSTANTS } from "../../constants/notification/uploadNotificationConstants";
import moment from "moment";
import SWITCH_CAMERA_ICON from "../../../assets/icons/single color icons - svg/refresh-ccw.svg";
import { debounce } from "../../utils/debounce";

const { CAPTURE, CANCEL, SAVE, PLEASE_WAIT } = DocumentUploadConstants;

const { CAMERA_ACCESS_DENIED } = UPLOAD_NOTIFICATION_CONSTANTS;

export interface ICapturedFile {
  name?: string;
  format?: string;
  fileUrl?: string;
  file?: File;
}

export interface ICameraComponent {
  isVisible?: boolean;
  onSave?: (capturedFile?: ICapturedFile) => void | undefined;
  cameraOptions?: MediaStreamConstraints;
  closeCamera?: () => void;
  format?: string;
  fileName?: string;
  fileCompression?: number;
}

const CameraComponent = (props?: ICameraComponent) => {
  const {
    fileCompression = 0.95,
    format,
    fileName,
    onSave,
    cameraOptions,
    isVisible,
    closeCamera,
  } = props || {};

  const [isCameraActive, setIsCameraActive] = useState<boolean>(false);
  const [capturedFile, setCapturedFile] = useState<ICapturedFile | undefined>(
    undefined
  );
  const [isEnvCamera, setIsEnvCamera] = useState<boolean>(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const generateFileName = () => `VUKA_IMG_${moment().utc().valueOf()}.jpg`;

  const stopCamera = (shouldCloseCamera?: boolean) => {
    const stream = videoRef.current?.srcObject as MediaStream;
    if (!videoRef.current || !stream) {
      setIsCameraActive(false);
      if (shouldCloseCamera && closeCamera) {
        setCapturedFile(undefined);
        setIsEnvCamera(false);
        closeCamera();
      }
      return;
    }

    const tracks = stream.getTracks();
    tracks.forEach((track) => track.stop());
    videoRef.current.srcObject = null;
    setIsCameraActive(false);

    if (shouldCloseCamera && closeCamera) {
      setCapturedFile(undefined);
      setIsEnvCamera(false);
      closeCamera();
    }
  };

  const openCamera = async (forceIsCameraActive?: boolean) => {
    setCapturedFile(undefined);
    if (forceIsCameraActive ? false : isCameraActive) return;

    try {
      stopCamera(false);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1920, min: 426, max: 7680 },
          height: { ideal: 1080, min: 240, max: 4320 },
          frameRate: { ideal: 60, min: 24, max: 120 },
          autoGainControl: false,
          aspectRatio: { ideal: 16 / 9, min: 4 / 3, max: 21 / 9 },
          noiseSuppression: false,
          facingMode: isEnvCamera ? "environment" : "user",
        },
        audio: false,
        ...cameraOptions,
      });

      const video = videoRef.current;
      if (!video) {
        if (closeCamera) closeCamera();
        return;
      }

      video.srcObject = stream;
      await video.play();
      setIsCameraActive(true);
    } catch {
      Notification(CAMERA_ACCESS_DENIED);
      stopCamera(true);
    }
  };

  const capturePhoto = async () => {
    if (!isCameraActive) {
      await openCamera();
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas || !video.srcObject) {
      stopCamera(true);
      return;
    }

    try {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const context = canvas.getContext("2d");
      if (!context) {
        stopCamera(true);
        return;
      }

      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const capturedFileFormat = format ?? "image/jpeg";
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            stopCamera(true);
            return;
          }

          const capturedFileName = fileName ?? generateFileName();
          const file = new File([blob], capturedFileName, {
            type: capturedFileFormat,
          });
          setCapturedFile({
            name: capturedFileName,
            file,
            format: capturedFileFormat,
            fileUrl: URL.createObjectURL(file),
          });
          stopCamera(false);
        },
        capturedFileFormat,
        fileCompression
      );
    } catch {
      stopCamera(true);
    }
  };

  const handleSwitchCamera = () =>
    setIsEnvCamera((prevIsUserCamera) => !prevIsUserCamera);

  const handleCancelClick = async () => {
    if (capturedFile) {
      await openCamera();
    } else stopCamera(true);
  };

  const handleSaveFile = () => {
    if (onSave && capturedFile) onSave(capturedFile);
    stopCamera(true);
  };

  useEffect(() => {
    return () => stopCamera(false);
  }, []);

  useEffect(() => (isCameraActive && openCamera(true), void 0), [isEnvCamera]);

  useEffect(() => {
    if (isVisible) openCamera();
    else stopCamera(true);
  }, [isVisible]);

  return (
    <div className="camera-component">
      <Spin tip={PLEASE_WAIT} spinning={!capturedFile && !isCameraActive}>
        <div className="camera-component__video-container">
          <video
            ref={videoRef}
            className={`camera-component__video-container__video ${
              isEnvCamera ? "" : "flip-container"
            }`}
            playsInline
            autoPlay
            muted
          />
          {isCameraActive ? (
            <img
              onClick={debounce(handleSwitchCamera)}
              src={SWITCH_CAMERA_ICON}
              className="camera-component__video-container__switch-camera"
              alt="switch-camera-icon"
            />
          ) : null}
          <canvas
            ref={canvasRef}
            className={`camera-component__video-container__canvas ${
              capturedFile ? "" : "display-none"
            } ${isEnvCamera ? "" : "flip-container"}`}
          />
        </div>
      </Spin>
      <div className="camera-component__button-container">
        <Button
          className="camera-component__button-container__cancel-btn"
          type="primary"
          onClick={handleCancelClick}
        >
          {CANCEL}
        </Button>
        <Button
          type="primary"
          onClick={capturePhoto}
          disabled={!isCameraActive}
        >
          {CAPTURE}
        </Button>
      </div>
      <Button
        className="camera-component__save-btn"
        type="primary"
        onClick={handleSaveFile}
        disabled={!capturedFile}
      >
        {SAVE}
      </Button>
    </div>
  );
};

export default CameraComponent;
